import { appStore } from '../core/store';
import { APP_VERSION, BUILD_DATE } from '../core/version';
import { confirmDialog } from '../ui/confirm';
import { showToast } from '../ui/toast';
import { getUpdateManager, type UpdateManager } from '../core/updateManager';
import { checkGithubForNewerCommit } from '../core/githubUpdateCheck';
import { getSharedController } from './performance';
import { renderAudioRoutingTest } from './audioRoutingTest';
import { getCurrentSettingsTab, navigateToSettingsTab, type SettingsTab } from '../ui/router';
import { authStore } from '../core/authStore';
import { supabaseConfigured } from '../core/supabaseClient';
import { createBand, listMyBands, type MyBand } from '../core/bandsApi';
import { listBandSongs, type SyncedSongSummary } from '../core/songSync';
import { syncCloudLibraryMetadata } from '../core/cloudLibrarySync';
import { syncPersonalMyNotes } from '../core/personalMyNotesSync';
import { clearSyncDiagnostics, getSyncDiagnostics, subscribeSyncDiagnostics, type SyncDiagnosticEvent } from '../core/syncDiagnostics';

export function renderSettingsSection(container: HTMLElement): () => void {
  container.innerHTML = '';

  const shell = document.createElement('div');
  shell.innerHTML = `
    <div class="route-page-header settings-page-header">
      <div>
        <h1>Settings</h1>
        <p class="text-dim">Build info, audio routing, account sync, and app credits.</p>
      </div>
    </div>
    <div class="route-subnav settings-subnav" id="settings-subnav">
      <button class="btn" data-tab="diagnostics">Diagnostics</button>
      <button class="btn" data-tab="account">Account</button>
      <button class="btn" data-tab="about">About</button>
    </div>
    <div id="settings-tab-content"></div>
  `;
  container.appendChild(shell);

  const subnav = shell.querySelector<HTMLDivElement>('#settings-subnav')!;
  const tabContent = shell.querySelector<HTMLDivElement>('#settings-tab-content')!;

  let currentTabCleanup: (() => void) | null = null;

  function renderTab(tab: SettingsTab): void {
    currentTabCleanup?.();
    for (const btn of subnav.querySelectorAll<HTMLButtonElement>('button')) {
      btn.classList.toggle('btn-primary', btn.dataset.tab === tab);
    }
    currentTabCleanup =
      tab === 'account'
        ? renderAccountTab(tabContent)
        : tab === 'about'
          ? renderAboutTab(tabContent)
          : renderDiagnosticsTab(tabContent);
  }

  subnav.addEventListener('click', (e) => {
    const target = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-tab]');
    const tab = target?.dataset.tab as SettingsTab | undefined;
    if (!tab) return;
    navigateToSettingsTab(tab);
  });

  renderTab(getCurrentSettingsTab());

  return () => {
    currentTabCleanup?.();
  };
}

function renderDiagnosticsTab(container: HTMLElement): () => void {
  container.innerHTML = '';

  const wrapper = document.createElement('div');
  wrapper.innerHTML = `
    <div class="card" id="app-card"></div>

    <div class="card" id="audio-routing-test-card"></div>

    <div class="card">
      <h2 style="margin-top:0">Danger Zone</h2>
      <p class="text-dim">This permanently deletes all songs, audio files, and setlists stored on this tablet.</p>
      <button class="btn btn-danger" id="clear-data-btn">Clear All Local Data</button>
    </div>
  `;
  container.appendChild(wrapper);

  const appCard = wrapper.querySelector<HTMLDivElement>('#app-card')!;
  const clearDataBtn = wrapper.querySelector<HTMLButtonElement>('#clear-data-btn')!;
  const audioRoutingCard = wrapper.querySelector<HTMLDivElement>('#audio-routing-test-card')!;
  const cleanupAudioRoutingTest = renderAudioRoutingTest(audioRoutingCard);

  clearDataBtn.addEventListener('click', async () => {
    const confirmed = await confirmDialog({
      title: 'Clear All Local Data',
      message: 'This will permanently delete every song, audio file, and setlist stored on this tablet. This cannot be undone.',
      confirmLabel: 'Delete Everything',
      danger: true,
      requireTypedConfirmation: 'DELETE',
    });
    if (!confirmed) return;
    await appStore.clearAllData();
    showToast('All local data has been cleared.', 'warn');
  });

  function renderUpdateCard(): void {
    const manager = getUpdateManager();
    const isPlaying = getSharedController()?.getState().status === 'PLAYING';
    const updateAvailable = manager?.isUpdateAvailable() ?? false;

    appCard.innerHTML = `
      <h2 style="margin-top:0">App</h2>
      <p>Build: <strong>${APP_VERSION}</strong></p>
      <p class="text-dim">Built: ${new Date(BUILD_DATE).toLocaleString()}</p>
      ${
        updateAvailable
          ? `<p class="text-warn"><strong>Update available.</strong></p>
             <button class="btn btn-primary" id="apply-update-btn" ${isPlaying ? 'disabled' : ''}>Apply Update</button>
             ${isPlaying ? '<p class="text-warn">Cannot apply update while audio is playing. Stop playback first.</p>' : ''}`
          : `<p class="text-dim">You are running the latest downloaded version.</p>`
      }
      ${manager ? '<button class="btn btn-small" id="check-update-btn" style="margin-top:8px">Check for Update</button>' : ''}
    `;
    appCard.querySelector('#apply-update-btn')?.addEventListener('click', () => {
      manager?.applyUpdate();
    });
    const checkUpdateBtn = appCard.querySelector<HTMLButtonElement>('#check-update-btn');
    checkUpdateBtn?.addEventListener('click', () => {
      void handleCheckForUpdate(checkUpdateBtn, manager);
    });
  }

  async function handleCheckForUpdate(button: HTMLButtonElement, manager: UpdateManager | null): Promise<void> {
    if (!manager) return;
    if (!navigator.onLine) {
      showToast('You are offline — cannot check for updates.', 'warn');
      return;
    }
    button.disabled = true;
    button.textContent = 'Checking…';
    // Run both checks together: the service worker's own byte-diff check
    // (the one that actually matters for whether Apply Update appears) and
    // a live look at GitHub's latest commit on main, which can tell us
    // code has changed even in the window before Netlify finishes
    // deploying it — see checkGithubForNewerCommit's own doc comment.
    const [, githubStatus] = await Promise.all([manager.checkForUpdate(), checkGithubForNewerCommit(APP_VERSION)]);
    // registration.update() only resolves once the new worker script has
    // been fetched and compared, not once it's finished installing — give
    // the 'updatefound'/'statechange' listener a brief window to flip
    // isUpdateAvailable() (which re-renders this whole card) before falling
    // back to telling the user nothing new was found.
    await new Promise((resolve) => setTimeout(resolve, 1500));
    if (manager.isUpdateAvailable()) return;
    showToast(
      githubStatus?.isNewer
        ? "A newer version was just pushed to GitHub — it can take a minute or two to finish deploying. Try again shortly."
        : "You're running the latest version.",
      'info',
    );
    button.disabled = false;
    button.textContent = 'Check for Update';
  }

  const unsubUpdate = getUpdateManager()?.subscribe(renderUpdateCard) ?? (() => {});
  const controllerUnsub = getSharedController()?.subscribe(renderUpdateCard) ?? (() => {});

  renderUpdateCard();

  return () => {
    cleanupAudioRoutingTest();
    unsubUpdate();
    controllerUnsub();
  };
}

function renderAboutTab(container: HTMLElement): () => void {
  container.innerHTML = '';

  const wrapper = document.createElement('div');
  wrapper.innerHTML = `
    <div class="card">
      <h2 style="margin-top:0">Maximum Band</h2>
      <p><strong>Practice better. Play together. Hit Maximum.</strong></p>
      <p class="text-dim">
        A practice and show-prep player for songs, clicks, setlists, markers, and notes.
      </p>
      <p class="text-dim">
        Created by <strong>Conrad Tolosa</strong>.
      </p>
    </div>
  `;
  container.appendChild(wrapper);

  return () => {};
}

function escapeHtml(value: string): string {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

/** Sign-in UI — magic link only for now (see SUPABASE_SPEC.md §6/§8; Google
 * sign-in needs its own separate OAuth-client setup before it can be added
 * here). Re-renders on every authStore change, not just on mount, since a
 * magic-link click typically completes the sign-in in a DIFFERENT browser
 * tab than the one that requested it — Supabase's own cross-tab session
 * sync (via localStorage) is what brings this tab's UI up to date. */
function renderAccountTab(container: HTMLElement): () => void {
  container.innerHTML = '';

  const wrapper = document.createElement('div');
  container.appendChild(wrapper);

  let sendingLink = false;
  let linkSentTo: string | null = null;
  let syncingCloud = false;
  let lastSyncMessage: string | null = null;

  // Bands section state — separate from the account-card state above since
  // it only applies once signed in, and its data comes from an async fetch
  // rather than being available synchronously like authStore's session.
  let bandsLoadedForUserId: string | null = null;
  let bandsState: { loading: boolean; bands: MyBand[]; error: string | null } = { loading: true, bands: [], error: null };
  let creatingBand = false;
  let showCreateBandForm = false;

  // Which band (if any) has its synced-songs list expanded, and a
  // per-band cache of that list — read-only, purely informational (see
  // songSync.ts's own doc comment: nothing plays these back yet). Lazy per
  // band rather than fetched alongside the band list itself, since most
  // visits won't need to see any given band's song list at all.
  let expandedBandId: string | null = null;
  const bandSongsCache = new Map<string, { loading: boolean; songs: SyncedSongSummary[]; error: string | null }>();

  function render(): void {
    if (!supabaseConfigured) {
      wrapper.innerHTML = `
        <div class="card">
          <h2 style="margin-top:0">Account</h2>
          <p class="text-dim" style="margin-bottom:0">Accounts aren't set up in this build yet.</p>
        </div>
      `;
      return;
    }

    const session = authStore.getSession();

    if (session) {
      // Guard, not an effect run on every render: without this, every
      // re-render (e.g. from the create-band form's own local state
      // changes) would restart the fetch and flash back to "Loading…".
      // Keyed by user id, not just "already loaded once", so signing out
      // and back in as someone else re-fetches instead of showing the
      // previous person's bands.
      if (bandsLoadedForUserId !== session.user.id) {
        bandsLoadedForUserId = session.user.id;
        bandsState = { loading: true, bands: [], error: null };
        void loadBands();
      }

      wrapper.innerHTML = `
        <div class="card">
          <h2 style="margin-top:0">Account</h2>
          <p>Signed in as <strong>${escapeHtml(session.user.email ?? 'unknown')}</strong></p>
          <button class="btn btn-primary" id="sync-cloud-now-btn" ${syncingCloud ? 'disabled' : ''}>${
            syncingCloud ? 'Syncing...' : 'Sync Now'
          }</button>
          <button class="btn" id="sign-out-btn">Sign Out</button>
          ${lastSyncMessage ? `<p class="text-dim" style="margin-bottom:0">${escapeHtml(lastSyncMessage)}</p>` : ''}
        </div>
        <div class="card" id="bands-card"></div>
        <div class="card" id="sync-diagnostics-card"></div>
      `;
      wrapper.querySelector('#sync-cloud-now-btn')?.addEventListener('click', () => void handleSyncNow());
      wrapper.querySelector('#sign-out-btn')?.addEventListener('click', async () => {
        await authStore.signOut();
        showToast('Signed out.', 'info');
      });
      renderBandsCard();
      renderSyncDiagnosticsCard();
      return;
    }

    wrapper.innerHTML = `
      <div class="card">
        <h2 style="margin-top:0">Sign In</h2>
        <p class="text-dim">Sign in with a magic link sent to your email — no password needed.</p>
        ${
          linkSentTo
            ? `<p class="text-warn">Check <strong>${escapeHtml(linkSentTo)}</strong> for a sign-in link.</p>
               <button class="btn btn-small" id="use-different-email-btn">Use a different email</button>`
            : `<div class="field">
                 <input type="email" id="account-email-input" placeholder="you@example.com" autocomplete="email" style="width:100%" />
               </div>
               <button class="btn btn-primary" id="send-link-btn" ${sendingLink ? 'disabled' : ''}>${
                 sendingLink ? 'Sending…' : 'Send Magic Link'
               }</button>`
        }
      </div>
    `;

    wrapper.querySelector('#use-different-email-btn')?.addEventListener('click', () => {
      linkSentTo = null;
      render();
    });

    const sendBtn = wrapper.querySelector<HTMLButtonElement>('#send-link-btn');
    const emailInput = wrapper.querySelector<HTMLInputElement>('#account-email-input');
    sendBtn?.addEventListener('click', () => void handleSendLink());
    emailInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') void handleSendLink();
    });

    async function handleSendLink(): Promise<void> {
      const email = emailInput?.value.trim();
      if (!email) {
        showToast('Enter an email address first.', 'warn');
        return;
      }
      sendingLink = true;
      render();
      const error = await authStore.sendMagicLink(email);
      sendingLink = false;
      if (error) {
        showToast(error, 'warn');
        render();
        return;
      }
      linkSentTo = email;
      render();
    }
  }

  async function handleSyncNow(): Promise<void> {
    if (syncingCloud) return;
    syncingCloud = true;
    lastSyncMessage = null;
    render();

    const cloudResult = await syncCloudLibraryMetadata();
    if (cloudResult.error) {
      syncingCloud = false;
      lastSyncMessage = cloudResult.error;
      showToast(cloudResult.error, 'warn');
      render();
      return;
    }

    await syncPersonalMyNotes();
    syncingCloud = false;
    lastSyncMessage =
      cloudResult.remappedSetlistEntries > 0
        ? `Remapped ${cloudResult.remappedSetlistEntries} setlist entr${cloudResult.remappedSetlistEntries === 1 ? 'y' : 'ies'} to the cloud-linked song.`
        : cloudResult.duplicateCloudSongs > 0
          ? `Found ${cloudResult.duplicateCloudSongs} duplicate cloud song(s); using the copy with markers.`
          : cloudResult.addedSongs > 0
            ? `Pulled ${cloudResult.addedSongs} cloud song(s). Audio stays local on this device.`
            : 'Cloud songs and My Notes are up to date.';
    showToast(lastSyncMessage, 'info');
    render();
  }

  function renderSyncDiagnosticsCard(): void {
    const diagnosticsCard = wrapper.querySelector<HTMLDivElement>('#sync-diagnostics-card');
    if (!diagnosticsCard) return;
    const events = getSyncDiagnostics().slice(0, 20);
    diagnosticsCard.innerHTML = `
      <div style="display:flex; align-items:center; justify-content:space-between; gap:12px">
        <h2 style="margin-top:0">Sync Diagnostics</h2>
        <button class="btn btn-small" id="clear-sync-diagnostics-btn" ${events.length === 0 ? 'disabled' : ''}>Clear</button>
      </div>
      ${
        events.length === 0
          ? '<p class="text-dim" style="margin-bottom:0">No sync events recorded on this device yet.</p>'
          : `<ol style="margin:0; padding-left:20px">
              ${events.map(renderSyncDiagnosticEvent).join('')}
            </ol>`
      }
    `;
    diagnosticsCard.querySelector('#clear-sync-diagnostics-btn')?.addEventListener('click', () => {
      clearSyncDiagnostics();
      renderSyncDiagnosticsCard();
    });
  }

  function renderSyncDiagnosticEvent(event: SyncDiagnosticEvent): string {
    const detailRows = Object.entries(event.details)
      .map(([key, value]) => `<div><code>${escapeHtml(key)}</code>: <code>${escapeHtml(String(value))}</code></div>`)
      .join('');
    return `
      <li style="margin:0 0 12px">
        <div><strong>${escapeHtml(event.source)}</strong> <span class="text-dim">${escapeHtml(new Date(event.at).toLocaleString())}</span></div>
        <div class="${event.level === 'error' || event.level === 'warn' ? 'text-warn' : 'text-dim'}">${escapeHtml(event.message)}</div>
        <div style="font-size:0.85rem; line-height:1.4; overflow-wrap:anywhere">${detailRows}</div>
      </li>
    `;
  }

  async function loadBands(): Promise<void> {
    const result = await listMyBands();
    bandsState = { loading: false, bands: result.bands, error: result.error };
    renderBandsCard();
  }

  function renderBandsCard(): void {
    const bandsCard = wrapper.querySelector<HTMLDivElement>('#bands-card');
    if (!bandsCard) return; // signed out mid-fetch (e.g. tab left open) — nothing to update

    const listHtml = bandsState.loading
      ? '<p class="text-dim">Loading…</p>'
      : bandsState.error
        ? `<p class="text-warn">${escapeHtml(bandsState.error)}</p>`
        : bandsState.bands.length === 0
          ? '<p class="text-dim">You\'re not in any bands yet.</p>'
          : `<ul style="margin:0; padding-left:20px; list-style:none">
               ${bandsState.bands.map((band) => renderBandRow(band)).join('')}
             </ul>`;

    bandsCard.innerHTML = `
      <h2 style="margin-top:0">My Bands</h2>
      ${listHtml}
      ${
        showCreateBandForm
          ? `<div class="field" style="margin-top:12px">
               <input type="text" id="band-name-input" placeholder="Band name" style="width:100%" />
             </div>
             <button class="btn btn-primary" id="create-band-submit-btn" ${creatingBand ? 'disabled' : ''}>${
               creatingBand ? 'Creating…' : 'Create'
             }</button>
             <button class="btn btn-small" id="create-band-cancel-btn">Cancel</button>`
          : `<button class="btn" id="create-band-btn" style="margin-top:12px">Create a Band</button>`
      }
    `;

    bandsCard.querySelectorAll<HTMLButtonElement>('button[data-expand-band-id]').forEach((btn) => {
      btn.addEventListener('click', () => void toggleBandExpanded(btn.dataset.expandBandId!));
    });

    bandsCard.querySelector('#create-band-btn')?.addEventListener('click', () => {
      showCreateBandForm = true;
      renderBandsCard();
      bandsCard.querySelector<HTMLInputElement>('#band-name-input')?.focus();
    });
    bandsCard.querySelector('#create-band-cancel-btn')?.addEventListener('click', () => {
      showCreateBandForm = false;
      renderBandsCard();
    });
    const nameInput = bandsCard.querySelector<HTMLInputElement>('#band-name-input');
    const submitBtn = bandsCard.querySelector<HTMLButtonElement>('#create-band-submit-btn');
    submitBtn?.addEventListener('click', () => void handleCreateBand());
    nameInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') void handleCreateBand();
    });

    async function handleCreateBand(): Promise<void> {
      const name = nameInput?.value.trim();
      if (!name) {
        showToast('Enter a band name first.', 'warn');
        return;
      }
      creatingBand = true;
      renderBandsCard();
      const { error } = await createBand(name);
      creatingBand = false;
      if (error) {
        showToast(error, 'warn');
        renderBandsCard();
        return;
      }
      showCreateBandForm = false;
      showToast(`"${name}" created.`, 'info');
      await loadBands();
    }
  }

  /** One band's row in the "My Bands" list, plus its synced-songs list when
   * expanded — read-only (see songSync.ts: nothing plays these back yet,
   * this is purely "did the sync actually work" visibility). */
  function renderBandRow(band: MyBand): string {
    const isExpanded = expandedBandId === band.id;
    const cached = bandSongsCache.get(band.id);
    const songsHtml = !isExpanded
      ? ''
      : `<div style="margin:4px 0 8px 20px">
           ${
             !cached || cached.loading
               ? '<p class="text-dim" style="margin:4px 0">Loading songs…</p>'
               : cached.error
                 ? `<p class="text-warn" style="margin:4px 0">${escapeHtml(cached.error)}</p>`
                 : cached.songs.length === 0
                   ? '<p class="text-dim" style="margin:4px 0">No songs synced to this band yet.</p>'
                   : `<ul style="margin:4px 0; padding-left:20px">
                        ${cached.songs
                          .map((s) => `<li>${escapeHtml(s.name)} <span class="text-dim">(${s.markerCount} marker${s.markerCount === 1 ? '' : 's'})</span></li>`)
                          .join('')}
                      </ul>`
           }
         </div>`;
    return `
      <li style="margin-bottom:4px">
        <button type="button" class="btn btn-small" data-expand-band-id="${band.id}" style="text-align:left">
          ${isExpanded ? '▾' : '▸'} ${escapeHtml(band.name)} <span class="text-dim">(${band.role})</span>
        </button>
        ${songsHtml}
      </li>
    `;
  }

  async function toggleBandExpanded(bandId: string): Promise<void> {
    if (expandedBandId === bandId) {
      expandedBandId = null;
      renderBandsCard();
      return;
    }
    expandedBandId = bandId;
    if (!bandSongsCache.has(bandId)) {
      bandSongsCache.set(bandId, { loading: true, songs: [], error: null });
      renderBandsCard();
      const result = await listBandSongs(bandId);
      bandSongsCache.set(bandId, { loading: false, songs: result.songs, error: result.error });
    }
    renderBandsCard();
  }

  render();
  const unsubscribe = authStore.subscribe(() => render());
  const unsubscribeDiagnostics = subscribeSyncDiagnostics(() => renderSyncDiagnosticsCard());

  return () => {
    unsubscribe();
    unsubscribeDiagnostics();
  };
}
